import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  ProductListConfigurationKeys,
  ProductListConfigurations,
  PRODUCT_LIST_NAMESPACE,
} from '@epicuro-next/constants/rule-engine/product-list-rules';
import { BasketPatchItem } from '@epicuro-next/data/baskets-dto';
import { APP_TOP_LEVEL_PATH } from '@epicuro-next/navigation/constants';
import {
  ConfigurationsService,
  initConfigSuccess,
  selectConfigurationValueFrom,
  selectProductIdAdditionalPrefixRexExp,
} from '@epicuro-next/platform/configuration';
import { LoggerService } from '@epicuro-next/platform/monitoring';
import { setActiveAccount } from '@epicuro-next/state/accounts-state';
import {
  BasketUpdateShops,
  resetBasket,
  selectBasketId,
  selectItemsInsertOrUpdate,
  selectOperableBasketOrderReferenceNumber,
  selectShouldShowRrpTotals,
  setBasketNotification,
  submitBasketSuccess,
  switchBasketSuccess,
  updateBasketItems,
  updateBasketItemsFailure,
  updateBasketItemsSuccess,
} from '@epicuro-next/state/basket-state';
import { loadDab } from '@epicuro-next/state/dab-state';
import { selectListEntitiesWithProducts } from '@epicuro-next/state/list-entities';
import { selectProducts } from '@epicuro-next/state/p-basket-state';
import {
  selectOpenedListProducts,
  setFullList,
  shopTheList,
} from '@epicuro-next/state/product-list-state';
import {
  closeOverlayOutlet,
  go,
  openOverlayOutlet,
} from '@epicuro-next/state/router-store';
import { selectLookbookListIdInRoute } from '@epicuro-next/state/shop-the-look-state';
import { MessageType, showSnackBarMessage } from '@epicuro-next/state/snackbar';
import { isEqual, isNonNullish, mapProductId } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, select, Store } from '@ngrx/store';
import {
  Product,
  ProductKey,
  ProductType,
} from 'graphql-api/gql-api-interfaces';
import { forkJoin, of } from 'rxjs';
import {
  concatMap,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  switchMap,
  switchMapTo,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import {
  ConfigStripStateConfigurationKeys,
  FEATURE_NAME,
} from '../../configurations';
import * as configStripActions from '../actions';
import { BackgroundProductState, ProductVariantEntities } from '../models';
import {
  selectBasketUpdateItems,
  selectCachedState,
  selectCurrentlySelectedVariantId,
  selectNextBackgroundProduct,
  selectProduct,
  selectSkipInventoryCheckEnabled,
  selectTotals,
  selectVariantEntities,
} from '../selectors';

export const basketUpdateFailureSnackBarConfig = {
  message: $localize`:@@addToBasketFailed:We are having trouble operating on your basket at the moment. A notification has been sent to the team, please try again later.`,
  messageType: MessageType.WARNING,
  hideDelay: 10000,
};

/**
 * Effective config strip side-effects
 */
@Injectable()
export class ConfigStripEffects {
  /** Dependencies */
  constructor(
    private actions$: Actions,
    private logger: LoggerService,
    private store: Store,
    private router: Router,
    private configService: ConfigurationsService,
  ) {
    this.configService
      .getConfigValue({
        namespace: FEATURE_NAME,
        featureName: ConfigStripStateConfigurationKeys.CategoryField,
        defaultValue: 'category',
      })
      .subscribe((value: string) => (this.categoryField = value));
  }

  private categoryField: string;

  private shopTheListDisabled$ = this.store.pipe(
    select(
      selectConfigurationValueFrom<
        ProductListConfigurationKeys.ShopTheListDisabled,
        ProductListConfigurations
      >(
        PRODUCT_LIST_NAMESPACE,
        ProductListConfigurationKeys.ShopTheListDisabled,
        false,
      ),
    ),
  );

  private nextBackgroundProduct$ = this.store.pipe(
    select(selectNextBackgroundProduct),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public setIfConfigStripOpen$ = createEffect(() =>
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event: NavigationEnd) => {
        return event.urlAfterRedirects.includes(APP_TOP_LEVEL_PATH.cs.path);
      }),
      distinctUntilChanged(),
      switchMap((open: boolean) => {
        const resultActions: Action<string>[] = [
          configStripActions.setConfigStripIsOpen({ open }),
        ];

        if (!open) {
          resultActions.push(configStripActions.clearState());
        }

        return resultActions;
      }),
    ),
  );

  public prepareBackgroundProductsOpenedFromPBasket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(configStripActions.openCSForPBasket),
      switchMap(() => this.store.select(selectProducts).pipe(take(1))),
      withLatestFrom(
        this.configService.getConfigValue({
          namespace: FEATURE_NAME,
          featureName: ConfigStripStateConfigurationKeys.ShowNextButton,
          defaultValue: false,
        }),
      ),
      switchMap(([products, showButton]: [Product[], boolean]) => [
        configStripActions.setBackgroundProducts({
          backgroundProducts: this.getBackgroundProducts(products),
        }),
        configStripActions.setNextButtonVisible({
          showButton,
        }),
      ]),
    ),
  );

  public prepareBackgroundProductsOpenedFromList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(configStripActions.openCSForList),
      switchMap(() =>
        this.store.select(selectOpenedListProducts).pipe(
          filter((products) => products.length > 0),
          distinctUntilChanged(isEqual),
        ),
      ),
      switchMap((products) => this.triggerBgProducts(products)),
    ),
  );

  public prepareBackgroundProductsOpenedFromLook$ = createEffect(() =>
    this.actions$.pipe(
      ofType(configStripActions.openCSForLook),
      switchMap(() => this.store.pipe(select(selectLookbookListIdInRoute))),
      switchMap((listId) =>
        this.store.select(selectListEntitiesWithProducts, [listId]).pipe(
          map((list) => list[0]?.products),
          take(1),
        ),
      ),
      switchMap((products) => this.triggerBgProducts(products)),
    ),
  );

  public setNextButtonVisible$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        configStripActions.openCSForList,
        configStripActions.openCSForLook,
        shopTheList,
      ),
      switchMapTo(this.shopTheListDisabled$),
      filter((shopTHeListDisabled) => !shopTHeListDisabled),
      switchMap(() => [
        configStripActions.setNextButtonVisible({
          showButton: true,
        }),
      ]),
    ),
  );

  public prepareProductsForShopTheList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setFullList),
      map(({ list }) =>
        configStripActions.setBackgroundProducts({
          backgroundProducts: this.getBackgroundProducts(list),
        }),
      ),
    ),
  );

  public clearCachedConfigStripData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setActiveAccount),
      withLatestFrom(this.store.select(selectCachedState)),
      filter(
        ([
          {
            account: { accountId },
          },
          cachedState,
        ]) => accountId !== cachedState.accountId,
      ),
      switchMap(
        ([
          {
            account: { accountId },
          },
        ]) => this.getClearCacheActions(accountId),
      ),
    ),
  );

  public clearCachedConfigStripDataOnBasketSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitBasketSuccess),
      withLatestFrom(this.store.select(selectCachedState)),
      switchMap(([, { accountId }]) =>
        this.getClearCacheActions(accountId ?? ''),
      ),
    ),
  );

  public clearCachedConfigStripDataOnBasketChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(resetBasket, switchBasketSuccess),
      withLatestFrom(this.store.select(selectCachedState)),
      switchMap(([, { accountId }]) =>
        this.getClearCacheActions(accountId ?? ''),
      ),
    ),
  );

  public persistChanges$ = createEffect(() =>
    this.actions$.pipe(
      ofType(configStripActions.persistChanges),
      concatLatestFrom(() => this.store.pipe(select(selectBasketUpdateItems))),
      map(([_, basketItems]) => basketItems),
      filter((items: BasketUpdateShops) => !!Object.keys(items).length),
      map((items: BasketUpdateShops) => updateBasketItems(items)),
    ),
  );

  public loadProductSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(configStripActions.loadProductSuccess),
      map(({ product: { children, prepacks } }) => {
        const variantsKeys =
          children?.map(({ productId, brandId }) => ({
            productId,
            brandId,
          })) ?? [];
        const prepacksKeys =
          prepacks?.map(({ productId, brandId }) => ({
            productId,
            brandId,
          })) ?? [];
        return loadDab({ products: variantsKeys.concat(prepacksKeys) });
      }),
    ),
  );

  public loadPricesWhenProductsLoaded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(configStripActions.loadProductSuccess),
      map(({ product: { productId, brandId, prepacks } }) => {
        const prepackProductKeys: ProductKey[] =
          prepacks?.map((prepack) => ({
            productId: prepack.productId,
            brandId: prepack.brandId,
          })) ?? [];
        return configStripActions.loadPrices({
          products: [{ productId, brandId }, ...prepackProductKeys],
        });
      }),
    ),
  );

  public clearShopQuantities$ = createEffect(() =>
    this.actions$.pipe(
      ofType(configStripActions.clearShopQuantities),
      map(() => configStripActions.clearShopQuantitiesSuccess()),
    ),
  );

  public updateBasketItemsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateBasketItemsSuccess),
      map(() => configStripActions.persistChangesSuccess()),
    ),
  );

  public showBasketNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateBasketItems),
      switchMap(() =>
        forkJoin([
          this.store.pipe(select(selectProduct), filter(isNonNullish), take(1)),
          this.store.pipe(
            select(selectCurrentlySelectedVariantId),
            filter(isNonNullish),
            take(1),
          ),
          this.store.pipe(select(selectVariantEntities), take(1)),
          this.store.pipe(select(selectTotals), take(1)),
          this.store.pipe(select(selectShouldShowRrpTotals), take(1)),
          this.store.pipe(
            select(selectProductIdAdditionalPrefixRexExp),
            take(1),
          ),
          this.store.pipe(
            select(selectOperableBasketOrderReferenceNumber),
            take(1),
          ),
          this.store.pipe(
            select(selectItemsInsertOrUpdate),
            filter(isNonNullish),
            filter((items) => Object.keys(items).length > 0),
            take(1),
          ),
        ]),
      ),
      switchMap(
        ([
          product,
          currentVariant,
          variants,
          totals,
          shouldUseRrpPrices,
          additionalPrefixRegExp,
          orderReferenceNumber,
          itemsInsertOrUpdate,
        ]) =>
          this.actions$.pipe(
            ofType(updateBasketItemsSuccess),
            take(1),
            map(() => {
              const material = Object.values(variants).find(
                (v) => v.productType === ProductType.Material,
              );

              return setBasketNotification({
                brandId: product.brandId,
                currency: totals.currency,
                imageUrl:
                  variants[currentVariant]?.mainImageUrl ||
                  (product.mainImageUrl ?? product.parent?.mainImageUrl),
                materialId: material?.productId,
                parentId: material?.parentId ?? undefined,
                productId: mapProductId(
                  product.productId,
                  additionalPrefixRegExp,
                ),
                productTitle: (product as Product).title,
                totalQuantity: totals.quantity,
                totalRequestedPrice: shouldUseRrpPrices
                  ? totals.prices.rrp
                  : totals.prices.wsp,
                orderReferenceNumber: orderReferenceNumber as string,
                updatedVariants: groupPatchItemsWithVariants(
                  itemsInsertOrUpdate,
                  variants,
                ),
              });
            }),
          ),
      ),
    ),
  );

  public updateBasketItemsFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateBasketItemsFailure),
      tap((error) => {
        this.logger.exception({
          error: new Error('Update basket items failure: ' + error.error),
        });
      }),
      switchMap((error) => [
        showSnackBarMessage(basketUpdateFailureSnackBarConfig),
        configStripActions.persistChangesFailure(error),
      ]),
    ),
  );

  public handleConfigStripNextActionStrategy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(configStripActions.addButtonClickStrategyHandle),
      concatMap(({ navigateToNext }) =>
        forkJoin([
          of(navigateToNext),
          this.store.pipe(select(selectBasketId), filter(Boolean), take(1)),
        ]).pipe(
          concatLatestFrom(() => this.nextBackgroundProduct$),
          map(([[navigateNext, basketId], nextBackgroundProduct]) => {
            if (navigateNext && nextBackgroundProduct) {
              return configStripActions.goToNextProduct();
            } else if (navigateNext && !nextBackgroundProduct) {
              return go({
                commands: ['basket', basketId],
              });
            } else {
              return closeOverlayOutlet();
            }
          }),
        ),
      ),
    ),
  );

  public goToNextProduct$ = createEffect(() =>
    this.actions$.pipe(
      ofType(configStripActions.goToNextProduct),
      concatMap((action) =>
        of(action).pipe(
          concatLatestFrom(() => this.nextBackgroundProduct$),
          filter(([, nextBackgroundProduct]) =>
            isNonNullish(nextBackgroundProduct),
          ),
          map(([, nextBackgroundProduct]) => {
            const { productParentId, productId, brandId } =
              nextBackgroundProduct as BackgroundProductState;
            const ids: string[] = [productParentId, productId, productId]
              .filter(isNonNullish)
              .slice(0, 2);

            return ['cs', brandId, ...ids];
          }),
          map((overlayPath) => openOverlayOutlet({ overlayPath })),
        ),
      ),
    ),
  );

  public readonly setSkipInventoryCheck$ = createEffect(() =>
    this.actions$.pipe(
      ofType(initConfigSuccess),
      filter(({ configurations }) => {
        return Object.keys(configurations[FEATURE_NAME] ?? {}).includes(
          ConfigStripStateConfigurationKeys.SkipInventoryCheck,
        );
      }),
      concatLatestFrom(() =>
        this.store.pipe(select(selectSkipInventoryCheckEnabled), map(Boolean)),
      ),
      map(([_, skipInventoryCheck]) =>
        configStripActions.setSkipInventoryCheck({ skipInventoryCheck }),
      ),
    ),
  );

  private readonly getClearCacheActions = (accountId: string) => [
    configStripActions.updateCache({
      accountId,
    }),
    configStripActions.clearPrefilledDeliveryItems(),
  ];

  /** background products so CS knows which product to open next */
  private getBackgroundProducts(products: Product[]) {
    const backgroundProductIds = new Map<string, boolean>();
    const backgroundProducts: BackgroundProductState[] = [];

    products.forEach((product) => {
      const { productId, brandId, mainImageUrl, parent } = product;
      const articleProductId = parent?.productId ?? productId;
      // CS opens all variants of the same article, so Background products have to have only one
      // specific variants if it many in initial list
      if (!backgroundProductIds.has(articleProductId)) {
        backgroundProductIds.set(articleProductId, true);
        backgroundProducts.push({
          productId: productId ?? articleProductId,
          productParentId: articleProductId ?? productId,
          brandId,
          mainImageUrl,
          category: (product.attributes?.[this.categoryField] as string) || '',
        });
      }
    });
    return backgroundProducts;
  }

  private triggerBgProducts(products: Product[]) {
    return [
      configStripActions.setBackgroundProducts({
        backgroundProducts: this.getBackgroundProducts(products),
      }),
    ];
  }
}

export function groupPatchItemsWithVariants(
  patchItems: BasketPatchItem[],
  variants: ProductVariantEntities,
) {
  const updatedVariants: { [key: string]: number } = {};
  const variantEntries = Object.entries(variants);

  patchItems?.forEach((item) => {
    const [parentVariantId] =
      variantEntries.find(([, variant]) =>
        variant.children?.find(
          (variantChild) => variantChild.productId === item.productId,
        ),
      ) ?? [];
    if (parentVariantId) {
      if (updatedVariants[parentVariantId]) {
        updatedVariants[parentVariantId] =
          updatedVariants[parentVariantId] + item.requestedQuantity;
      } else {
        updatedVariants[parentVariantId] = item.requestedQuantity;
      }
    }
  });

  return updatedVariants;
}
